import React, { useState } from 'react';

const SkillAnalysis = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedRole, setSelectedRole] = useState('frontend');
  const [skillLevels, setSkillLevels] = useState({
    // Mock current skill levels (1-5 scale)
    javascript: 4,
    react: 4,
    html: 5,
    css: 5,
    typescript: 3,
    nodejs: 2,
    python: 1,
    git: 4,
    aws: 1,
    docker: 1,
    testing: 3,
    uiux: 3
  });

  // Target roles and their required skills
  const targetRoles = {
    frontend: {
      title: 'Frontend Developer',
      description: 'Specializes in client-side web development',
      requiredSkills: {
        javascript: { level: 4, importance: 'critical' },
        react: { level: 4, importance: 'critical' },
        html: { level: 5, importance: 'critical' },
        css: { level: 5, importance: 'critical' },
        typescript: { level: 3, importance: 'important' },
        git: { level: 3, importance: 'important' },
        testing: { level: 3, importance: 'important' },
        uiux: { level: 3, importance: 'nice-to-have' }
      }
    },
    fullstack: {
      title: 'Full Stack Developer',
      description: 'Handles both frontend and backend development',
      requiredSkills: {
        javascript: { level: 4, importance: 'critical' },
        react: { level: 4, importance: 'critical' },
        nodejs: { level: 4, importance: 'critical' },
        html: { level: 4, importance: 'important' },
        css: { level: 4, importance: 'important' },
        typescript: { level: 3, importance: 'important' },
        git: { level: 4, importance: 'important' },
        aws: { level: 3, importance: 'important' },
        docker: { level: 2, importance: 'nice-to-have' },
        testing: { level: 3, importance: 'important' }
      }
    },
    backend: {
      title: 'Backend Developer',
      description: 'Focuses on server-side logic and databases',
      requiredSkills: {
        nodejs: { level: 4, importance: 'critical' },
        python: { level: 3, importance: 'important' },
        javascript: { level: 3, importance: 'important' },
        aws: { level: 3, importance: 'important' },
        docker: { level: 3, importance: 'important' },
        git: { level: 4, importance: 'important' },
        testing: { level: 4, importance: 'important' }
      }
    }
  };

  // Skill categories and metadata
  const skillCategories = {
    frontend: ['javascript', 'react', 'html', 'css', 'typescript'],
    backend: ['nodejs', 'python', 'aws', 'docker'],
    tools: ['git', 'testing'],
    soft: ['uiux']
  };

  const skillMetadata = {
    javascript: { name: 'JavaScript', category: 'frontend' },
    react: { name: 'React.js', category: 'frontend' },
    html: { name: 'HTML5', category: 'frontend' },
    css: { name: 'CSS3', category: 'frontend' },
    typescript: { name: 'TypeScript', category: 'frontend' },
    nodejs: { name: 'Node.js', category: 'backend' },
    python: { name: 'Python', category: 'backend' },
    git: { name: 'Git & GitHub', category: 'tools' },
    aws: { name: 'AWS', category: 'backend' },
    docker: { name: 'Docker', category: 'backend' },
    testing: { name: 'Testing', category: 'tools' },
    uiux: { name: 'UI/UX Design', category: 'soft' }
  };

  // Calculate skill gaps for selected role
  const calculateSkillGaps = () => {
    const gaps = [];
    const role = targetRoles[selectedRole];
    
    Object.entries(role.requiredSkills).forEach(([skill, requirement]) => {
      const currentLevel = skillLevels[skill] || 0;
      const gap = requirement.level - currentLevel;
      
      if (gap > 0) {
        gaps.push({
          skill,
          name: skillMetadata[skill].name,
          currentLevel,
          requiredLevel: requirement.level,
          gap,
          importance: requirement.importance
        });
      }
    });

    return gaps.sort((a, b) => b.gap - a.gap);
  };

  // Calculate match percentage for selected role
  const calculateMatchPercentage = () => {
    const role = targetRoles[selectedRole];
    let totalWeight = 0;
    let achievedWeight = 0;

    Object.entries(role.requiredSkills).forEach(([skill, requirement]) => {
      const weight = requirement.importance === 'critical' ? 3 : 
                    requirement.importance === 'important' ? 2 : 1;
      
      totalWeight += weight * requirement.level;
      const currentLevel = Math.min(skillLevels[skill] || 0, requirement.level);
      achievedWeight += weight * currentLevel;
    });

    return Math.round((achievedWeight / totalWeight) * 100);
  };

  // Learning resources based on skill gaps
  const learningResources = {
    javascript: [
      { name: 'JavaScript Modern Tutorial', provider: 'MDN Web Docs', duration: '40 hours', free: true },
      { name: 'JavaScript Algorithms and Data Structures', provider: 'freeCodeCamp', duration: '300 hours', free: true },
      { name: 'Modern JavaScript From The Beginning', provider: 'Udemy', duration: '21 hours', free: false }
    ],
    react: [
      { name: 'React Official Tutorial', provider: 'React.js', duration: '10 hours', free: true },
      { name: 'Full Modern React Tutorial', provider: 'The Net Ninja', duration: '15 hours', free: true },
      { name: 'React - The Complete Guide', provider: 'Udemy', duration: '48 hours', free: false }
    ],
    typescript: [
      { name: 'TypeScript Handbook', provider: 'TypeScript', duration: '15 hours', free: true },
      { name: 'Understanding TypeScript', provider: 'Udemy', duration: '16 hours', free: false }
    ],
    nodejs: [
      { name: 'Node.js Tutorial for Beginners', provider: 'YouTube', duration: '8 hours', free: true },
      { name: 'The Complete Node.js Developer Course', provider: 'Udemy', duration: '35 hours', free: false }
    ]
  };

  const skillGaps = calculateSkillGaps();
  const matchPercentage = calculateMatchPercentage();

  const getSkillLevelLabel = (level) => {
    const labels = ['Beginner', 'Basic', 'Intermediate', 'Advanced', 'Expert'];
    return labels[level - 1] || 'Unknown';
  };

  const getImportanceColor = (importance) => {
    const colors = {
      'critical': 'bg-red-100 text-red-800',
      'important': 'bg-orange-100 text-orange-800',
      'nice-to-have': 'bg-blue-100 text-blue-800'
    };
    return colors[importance] || 'bg-gray-100 text-gray-800';
  };

  const renderSkillBar = (currentLevel, requiredLevel, skill) => {
    return (
      <div className="flex items-center gap-4">
        <div className="w-32">
          <span className="text-sm font-medium text-gray-700">{skillMetadata[skill].name}</span>
        </div>
        <div className="flex-1">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Current: {getSkillLevelLabel(currentLevel)}</span>
            <span className="text-gray-600">Target: {getSkillLevelLabel(requiredLevel)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-green-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${(currentLevel / 5) * 100}%` }}
            ></div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1 mt-1 -mb-1">
            <div 
              className="bg-blue-500 h-1 rounded-full"
              style={{ width: `${(requiredLevel / 5) * 100}%` }}
            ></div>
          </div>
        </div>
        <div className="w-20 text-right">
          <span className={`text-sm font-medium ${
            currentLevel >= requiredLevel ? 'text-green-600' : 'text-red-600'
          }`}>
            {currentLevel >= requiredLevel ? '✓ Met' : `+${requiredLevel - currentLevel}`}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Skill Analysis</h1>
        <p className="text-gray-400">Analyze your skills, identify gaps, and create a personalized development plan</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <i className="fas fa-chart-bar text-blue-500 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Skills Assessed</p>
              <p className="text-2xl font-bold text-gray-900">{Object.keys(skillLevels).length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <i className="fas fa-bullseye text-green-500 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Role Match</p>
              <p className="text-2xl font-bold text-gray-900">{matchPercentage}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <i className="fas fa-exclamation-triangle text-orange-500 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Skill Gaps</p>
              <p className="text-2xl font-bold text-gray-900">{skillGaps.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <i className="fas fa-clock text-purple-500 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Est. Learning</p>
              <p className="text-2xl font-bold text-gray-900">45h</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content - 3/4 width */}
        <div className="lg:col-span-3">
          {/* Role Selection & Tabs */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Target Role Analysis</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(targetRoles).map(([key, role]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedRole(key)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedRole === key
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {role.title}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'overview'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('gaps')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'gaps'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Skill Gaps
                </button>
                <button
                  onClick={() => setActiveTab('learning')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'learning'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Learning Plan
                </button>
              </div>
            </div>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Role Match Card */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                      {targetRoles[selectedRole].title} - {matchPercentage}% Match
                    </h3>
                    <p className="text-gray-400">{targetRoles[selectedRole].description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-gray-800">{matchPercentage}%</div>
                    <div className="text-sm text-gray-600">Overall Match</div>
                  </div>
                </div>

                {/* Match Visualization */}
                <div className="space-y-4">
                  {Object.entries(targetRoles[selectedRole].requiredSkills).map(([skill, requirement]) => (
                    <div key={skill}>
                      {renderSkillBar(skillLevels[skill] || 0, requirement.level, skill)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Skill Categories - FIXED ALIGNMENT */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Skill Categories</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(skillCategories).map(([category, skills]) => (
                    <div key={category} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-3 capitalize">{category} Skills</h4>
                      <div className="space-y-3">
                        {skills.map(skill => {
                          const currentLevel = skillLevels[skill] || 0;
                          const requirement = targetRoles[selectedRole].requiredSkills[skill];
                          const meetsRequirement = requirement ? currentLevel >= requirement.level : true;
                          
                          return (
                            <div key={skill} className="flex justify-between items-center">
                              <span className="text-sm text-gray-700 flex-1 min-w-0 pr-2">
                                {skillMetadata[skill].name}
                              </span>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <div className="flex gap-1">
                                  {[1, 2, 3, 4, 5].map(level => (
                                    <div
                                      key={level}
                                      className={`w-2 h-2 rounded-full ${
                                        level <= currentLevel 
                                          ? meetsRequirement ? 'bg-green-500' : 'bg-yellow-500'
                                          : 'bg-gray-200'
                                      }`}
                                    ></div>
                                  ))}
                                </div>
                                <span className="text-xs text-gray-500 w-12 text-right">
                                  {getSkillLevelLabel(currentLevel)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'gaps' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-6">Identified Skill Gaps</h3>
              
              {skillGaps.length === 0 ? (
                <div className="text-center py-8">
                  <i className="fas fa-check-circle text-green-500 text-4xl mb-3"></i>
                  <h4 className="text-lg font-medium text-gray-800 mb-2">No Skill Gaps Found!</h4>
                  <p className="text-gray-400">Your skills meet all requirements for this role.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {skillGaps.map((gap, index) => (
                    <div key={gap.skill} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                            <i className="fas fa-exclamation-triangle text-red-500"></i>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800">{gap.name}</h4>
                            <span className={`text-xs px-2 py-1 rounded-full ${getImportanceColor(gap.importance)}`}>
                              {gap.importance.replace('-', ' ')}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-red-600">+{gap.gap} levels</div>
                          <div className="text-sm text-gray-600">Improvement needed</div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Current Level:</span>
                          <span className="ml-2 font-medium">{getSkillLevelLabel(gap.currentLevel)}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Required Level:</span>
                          <span className="ml-2 font-medium">{getSkillLevelLabel(gap.requiredLevel)}</span>
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-red-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${(gap.currentLevel / gap.requiredLevel) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'learning' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-6">Personalized Learning Plan</h3>
              
              {skillGaps.length === 0 ? (
                <div className="text-center py-8">
                  <i className="fas fa-graduation-cap text-green-500 text-4xl mb-3"></i>
                  <h4 className="text-lg font-medium text-gray-800 mb-2">No Learning Needed!</h4>
                  <p className="text-gray-400">Your skills are already at the required level.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {skillGaps.map((gap) => (
                    <div key={gap.skill} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-gray-800">{gap.name} Learning Path</h4>
                        <span className="text-sm text-gray-500">Level {gap.currentLevel} → {gap.requiredLevel}</span>
                      </div>
                      
                      <div className="space-y-3">
                        {(learningResources[gap.skill] || []).map((resource, index) => (
                          <div key={index} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <i className="fas fa-book text-blue-500"></i>
                              </div>
                              <div>
                                <p className="font-medium text-gray-800 text-sm">{resource.name}</p>
                                <p className="text-xs text-gray-600">{resource.provider} • {resource.duration}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {resource.free && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Free</span>
                              )}
                              <button className="text-blue-500 hover:text-blue-700 text-sm font-medium">
                                Explore →
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {(!learningResources[gap.skill] || learningResources[gap.skill].length === 0) && (
                        <div className="text-center py-4 text-gray-500">
                          <i className="fas fa-search mb-2"></i>
                          <p>No specific resources found. Check general programming courses.</p>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Overall Learning Plan */}
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-3">Recommended Learning Strategy</h4>
                    <div className="space-y-2 text-sm text-blue-700">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-clock"></i>
                        <span>Estimated completion time: 45 hours</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="fas fa-fire"></i>
                        <span>Focus on critical skills first: {skillGaps.filter(g => g.importance === 'critical').map(g => g.name).join(', ')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="fas fa-calendar"></i>
                        <span>Recommended pace: 5 hours per week</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar - 1/4 width */}
        <div className="lg:col-span-1">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="font-semibold text-gray-800 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="fas fa-sync-alt text-blue-500"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 text-sm">Update Skill Levels</p>
                    <p className="text-xs text-gray-600">Refresh your skill assessment</p>
                  </div>
                </div>
              </button>
              
              <button className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <i className="fas fa-download text-green-500"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 text-sm">Export Report</p>
                    <p className="text-xs text-gray-600">Download skill analysis PDF</p>
                  </div>
                </div>
              </button>
              
              <button className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <i className="fas fa-share-alt text-purple-500"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 text-sm">Share with Mentor</p>
                    <p className="text-xs text-gray-600">Get feedback on your plan</p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Market Insights */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Market Insights</h3>
            <div className="space-y-4">
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-1">
                  <i className="fas fa-arrow-up text-green-500"></i>
                  <span className="font-medium text-green-800 text-sm">High Demand</span>
                </div>
                <p className="text-xs text-green-700">React and TypeScript skills are in high demand with 45% growth</p>
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <i className="fas fa-chart-line text-blue-500"></i>
                  <span className="font-medium text-blue-800 text-sm">Emerging Trend</span>
                </div>
                <p className="text-xs text-blue-700">Docker and AWS skills increasing in job requirements</p>
              </div>
              
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-2 mb-1">
                  <i className="fas fa-dollar-sign text-orange-500"></i>
                  <span className="font-medium text-orange-800 text-sm">Salary Impact</span>
                </div>
                <p className="text-xs text-orange-700">Full Stack skills can increase salary by 25%</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillAnalysis;